import { supabase } from '../lib/supabase';
import { onlyDigits, EnriquecimentoData } from './prospeccaoLista';

// ─────────────────────────────────────────────────────────────────────────────
// Serviço "Descobrir CNPJ" (Prospecção Geográfica) — wrapper da edge function
// `descobrir-cnpj` (busca reversa nome/cidade/DDD → CNPJ na Casa dos Dados) +
// confirmação manual da escolha. O CNPJ descoberto alimenta o enriquecimento
// Lemit já existente (ver enriquecerEmpresaRow/enriquecerSociosRow).
// ─────────────────────────────────────────────────────────────────────────────

/** Candidato retornado pela Casa dos Dados (subset útil do tipo_resultado=completo). */
export interface CnpjCandidato {
  cnpj: string;                 // só dígitos
  cnpj_raiz?: string;
  razao_social?: string;
  nome_fantasia?: string | null;
  situacao_cadastral?: string;
  matriz_filial?: string;
  endereco?: {
    cep?: string; logradouro?: string; numero?: string; bairro?: string;
    uf?: string; municipio?: string; ibge?: { latitude?: number; longitude?: number };
  };
  atividade_principal?: { codigo?: string; descricao?: string };
  quadro_societario?: Array<{ nome?: string; qualificacao_socio?: string; documento?: string }>;
  contato_telefonico?: Array<{ completo?: string; ddd?: string; numero?: string; tipo?: string }>;
  contato_email?: Array<{ email?: string }>;
  mei?: { optante?: boolean; cpf?: string };
  score: number;
  telefone_bateu: boolean;
  endereco_bateu?: boolean;
  cidade_bateu?: boolean | null;  // false = Receita registra outra cidade; null/undefined = sem como comparar
  matched_por: 'nome' | 'ddd';
}

export interface DescobertaResult {
  auto: CnpjCandidato | null;
  candidatos: CnpjCandidato[];
  estrategia: string;
  motivo?: string | null;         // explicação legível quando não achou nada
  tentativas?: string[];
  cached: boolean;
  gravado?: boolean;
}

/** Extrai a mensagem amigável do erro da edge function (corpo `{ error, message }`). */
async function extractMessage(res: any): Promise<string> {
  if (res?.data?.message) return res.data.message;
  try {
    const body = await (res?.error as any)?.context?.json?.();
    if (body?.message) return body.message;
    if (typeof body?.error === 'string') return body.error;
  } catch { /* corpo ilegível */ }
  return res?.error?.message || 'Erro ao descobrir o CNPJ.';
}

/** Chama a busca reversa. Lança Error com mensagem amigável (inclui teto/saldo). */
export async function descobrirCnpj(placeId: string, opts?: { force?: boolean }): Promise<DescobertaResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Sessão expirada. Faça login novamente.');

  const res = await supabase.functions.invoke('descobrir-cnpj', {
    body: { place_id: placeId, force: !!opts?.force },
  });
  if (res.error || res.data?.error) throw new Error(await extractMessage(res));
  return res.data as DescobertaResult;
}

/** Deriva melhor WhatsApp/email do candidato (bônus grátis já vindo da Casa dos Dados). */
export function contatosDoCandidato(c: CnpjCandidato): { melhor_whatsapp?: string; melhor_email?: string } {
  const cel = (c.contato_telefonico || []).find((t) => (t.tipo || '').toLowerCase() === 'celular');
  const tel = cel || (c.contato_telefonico || [])[0];
  const email = (c.contato_email || [])[0]?.email;
  return {
    melhor_whatsapp: tel?.completo ? onlyDigits(tel.completo) : undefined,
    melhor_email: email || undefined,
  };
}

/** Grava a escolha manual do usuário no modal de candidatos (custo já foi pago). */
export async function confirmarCnpjManual(
  placeId: string,
  candidato: CnpjCandidato,
  enrAtual: EnriquecimentoData | null,
): Promise<EnriquecimentoData> {
  const cnpjDescoberta = {
    ...(enrAtual?.cnpj_descoberta || { consultado_em: new Date().toISOString(), estrategia: 'nome', total: 0, candidatos: [] as CnpjCandidato[] }),
    escolhido: candidato,
    escolhido_por: 'manual' as const,
  };
  const enr: EnriquecimentoData = { ...(enrAtual || {}), cnpj_descoberta: cnpjDescoberta };
  const { error } = await supabase.from('empresas_prospectadas')
    .update({ cnpj: onlyDigits(candidato.cnpj), enriquecimento_data: enr, updated_at: new Date().toISOString() })
    .eq('place_id', placeId);
  if (error) throw new Error(error.message);
  return enr;
}

/**
 * Desfaz um CNPJ gravado errado (auto-match ou escolha manual equivocada).
 * Mantém os candidatos em cache (custo já pago; reconsultar não cobra de novo),
 * mas descarta o escolhido e os dados Lemit — que pertenciam ao CNPJ errado.
 */
export async function limparCnpjEscolhido(
  placeId: string,
  enrAtual: EnriquecimentoData | null,
): Promise<EnriquecimentoData> {
  const cd = { ...(enrAtual?.cnpj_descoberta || { consultado_em: new Date().toISOString(), estrategia: 'nome', total: 0, candidatos: [] as CnpjCandidato[] }) };
  delete (cd as { escolhido?: unknown }).escolhido;
  delete (cd as { escolhido_por?: unknown }).escolhido_por;
  const enr: EnriquecimentoData = { cnpj_descoberta: cd };
  const { error } = await supabase.from('empresas_prospectadas')
    .update({ cnpj: null, enriquecimento_data: enr, enriquecimento_status: 'nao', updated_at: new Date().toISOString() })
    .eq('place_id', placeId);
  if (error) throw new Error(error.message);
  return enr;
}
